/* eslint-disable no-nested-ternary */
import * as React from 'react';
import {
  Box, CircularProgress, IconButton, Typography,
} from '@mui/material';

import { PlusIcon, PlusSquareIcon } from 'assets/icons';
import { extensionToType } from 'utils/file-uploader';
import { UploadResponse, UploadScenarios } from 'interfaces/file-uploader';
import { IconLoadingButton } from 'components/molecules';
import useFileUpload from 'base/core/hooks/use-file-upload';
import { isStreamableVideoAvailable } from 'utils/common-utils';
import ContentMediaView from '../content-media-view';

import './index.scss';
import ProgressBar from '../progress-bar';

interface FileDragUploaderProps {
  textLabel: string,
  handleFile: (res: UploadResponse) => void,
  currentContentUrl: string,
  scenario: UploadScenarios,
  uploadAnotherText: string,
  currentMediaType: string,
  accept?: string,
  currentContentName?: string,
}

const FileDragUploader = ({
  handleFile, textLabel, currentContentUrl, scenario, uploadAnotherText,
  currentMediaType, accept, currentContentName,
}: FileDragUploaderProps) => {
  const hiddenFileInput: any = React.useRef(null);
  const [contentUrl, setContentUrl] = React.useState(currentContentUrl);
  const [mediaType, setMediaType] = React.useState(currentMediaType);
  const [uploading, setUploading] = React.useState(false);
  const [processing, setProcessing] = React.useState(false);
  const { progress, handleContentDataUpload } = useFileUpload();
  const [fileName, setFileName] = React.useState(currentContentName);

  const imagesVideoOnly = '.jpg, .jpeg, .png, .gif, .mp4, .flv, .mkv, .webm, .mov, .wmv';

  const handleFileWrapper = async (event: any) => {
    setUploading(true);
    const res = await handleContentDataUpload(
      event,
      currentContentUrl,
      scenario,
    );

    setTimeout(async () => {
      handleFile(res);
      setFileName(res.fileData?.fileName || `${res.name}${res.extension}`);
      setMediaType(extensionToType.get(res.extension) as string);
      if (res.category === 'video') {
        setContentUrl(res.url);
        setUploading(false);
        setProcessing(true);
        await isStreamableVideoAvailable(res.url);
        setProcessing(false);
      } else {
        setContentUrl(res.url);
        setUploading(false);
      }
    }, 3000);
  };
  // drag state
  const [dragActive, setDragActive] = React.useState(false);

  // handle drag events
  const handleDrag = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  const handleDrop = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileWrapper(e);
    }
  };
  // triggers when file is selected with click
  const handleChange = (e: any) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFileWrapper(e);
    }
  };

  const handleClick = () => {
    hiddenFileInput.current.click();
  };

  return (
    <>
      <Box />
      {uploading ? (
        <>
          <Box className="file-upload-on-upload">
            <div style={{ textAlign: 'center' }}>
              <CircularProgress />
              <Typography variant="body1" sx={{ color: 'primary.main', fontWeight: 700 }}>
                {textLabel}
              </Typography>
            </div>
          </Box>
          {progress > -1 && (
            <Box sx={{ mt: 2 }}>
              <ProgressBar progress={progress} />
            </Box>
          )}
        </>
      ) : (!contentUrl || contentUrl === ''
        ? (
          <Box
            onDragEnter={handleDrag}
            className={dragActive ? 'drag-active' : ''}
          >
            <input type="file" id="input-file-upload" aria-label="input-file-upload" onChange={handleChange} ref={hiddenFileInput} accept={scenario === UploadScenarios.sessionHighlightMedia ? imagesVideoOnly : accept} />
            <label id="label-file-upload" htmlFor="input-file-upload" className={dragActive ? 'drag-active' : ''}>
              <div style={{ textAlign: 'center' }}>
                <IconButton
                  aria-label="createsession"
                  sx={{
                    backgroundColor: 'primary.light',
                    p: 2.25,
                    mb: 2,
                    '&:hover': { backgroundColor: 'primary.light' },
                  }}
                  className="create-session-btn"
                  onClick={handleClick}
                >
                  <PlusSquareIcon sx={{ color: 'primary.main' }} />
                </IconButton>
                <Typography variant="body1" sx={{ color: 'primary.main', fontWeight: 700 }}>
                  {textLabel}
                </Typography>
              </div>
            </label>

            {dragActive && <div id="drag-file-element" onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop} />}
          </Box>
        ) : (
          <>
            <div className="file-upload-content-media-view d-flex align-items-center justify-content-center">
              <ContentMediaView url={contentUrl} mediaType={mediaType} fileName={fileName} />
            </div>
            {processing && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>Your video is processing..</Typography>
              </Box>
            )}
            <div className="mt-2">
              <input type="file" id="input-file-upload" aria-label="input-file-upload" onChange={handleChange} ref={hiddenFileInput} accept={scenario === UploadScenarios.sessionHighlightMedia ? imagesVideoOnly : accept} />
              <IconLoadingButton
                startIcon={<PlusIcon sx={{ color: 'primary.main' }} />}
                onClick={handleClick}
                loading={uploading}
              >
                {uploadAnotherText}
              </IconLoadingButton>
            </div>
          </>
        )
      )}
    </>

  );
};

FileDragUploader.defaultProps = {
  accept: '*',
  currentContentName: '',
};

export default FileDragUploader;
